/**
 * Iframe / webview environment probes.
 *
 * Why this exists:
 * The CometChat SDK relies on localStorage and IndexedDB for the auth token,
 * and on a WebSocket for realtime delivery. In a Shopify storefront wrapped
 * by a native mobile app (Kin → WKWebView), this app is a *third-party*
 * iframe. iOS partitions storage for cross-site iframes by default, and
 * older WKWebView builds fail to write to localStorage at all, which makes
 * `CometChatUIKit.login(uid)` either fail loudly or — worse — hang silently
 * after a successful network round-trip because it can't persist the token.
 *
 * This module gives us:
 *   1. A boolean `isStorageWritable()` check that catches `SecurityError` and
 *      QuotaExceeded so the app can show a useful error instead of spinning.
 *   2. A best-effort `requestStorageAccessIfNeeded()` that prompts iOS Safari
 *      to grant the iframe first-party storage when available.
 */

import { dlog } from "./debugLog";

export function isInIframe(): boolean {
  try {
    return window.top !== window.self;
  } catch {
    // Cross-origin SecurityError reading window.top means we're in an iframe.
    return true;
  }
}

/**
 * Returns true if we can write *and* read back a key from localStorage.
 * In partitioned-storage iframes this often fails silently — write succeeds,
 * read returns null — which is why we round-trip a value.
 */
export function isStorageWritable(): boolean {
  try {
    const k = "__cc_probe__";
    const v = String(Date.now());
    window.localStorage.setItem(k, v);
    const read = window.localStorage.getItem(k);
    window.localStorage.removeItem(k);
    return read === v;
  } catch (e) {
    dlog.warn("localStorage probe threw:", e);
    return false;
  }
}

/**
 * On iOS Safari (and now most webviews), cross-site iframes start with
 * partitioned storage. The Storage Access API lets the iframe ask the OS
 * for first-party storage, but it usually requires a user gesture. We
 * call this at app init anyway — when no gesture is present it just
 * resolves false and we fall back to the partitioned storage.
 */
export async function requestStorageAccessIfNeeded(): Promise<boolean> {
  // If we're not in an iframe, partitioning is not a concern.
  if (!isInIframe()) return true;

  const doc = document as Document & {
    hasStorageAccess?: () => Promise<boolean>;
    requestStorageAccess?: () => Promise<void>;
  };

  if (typeof doc.hasStorageAccess !== "function") {
    // Older browser — nothing to do.
    return isStorageWritable();
  }

  try {
    const has = await doc.hasStorageAccess();
    if (has) {
      dlog.info("Storage access already granted to iframe.");
      return true;
    }
  } catch (e) {
    dlog.warn("hasStorageAccess threw:", e);
  }

  if (typeof doc.requestStorageAccess !== "function") {
    return isStorageWritable();
  }

  try {
    await doc.requestStorageAccess();
    dlog.info("Storage access granted via requestStorageAccess().");
    return true;
  } catch (e) {
    // This is normal — most browsers require a user gesture.
    dlog.warn(
      "requestStorageAccess() denied (likely needs a user gesture):",
      e
    );
    return isStorageWritable();
  }
}

/**
 * Promise wrapper that rejects if `p` doesn't settle within `ms` milliseconds.
 * Used to prevent the login flow from spinning forever in a webview where
 * the SDK promise never resolves.
 */
export function withTimeout<T>(
  p: Promise<T>,
  ms: number,
  label: string
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => {
      reject(new Error(`Timed out after ${ms}ms: ${label}`));
    }, ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      }
    );
  });
}
